import {
  DEFAULT_STOREFRONT_COLOR_CHOICE,
  isStorefrontColorChoice,
  normalizeStorefrontColorChoice,
  resolveStorefrontColorSystem,
  serializeStorefrontColorSystem,
  type StorefrontColorChoice,
  type StorefrontColorTokens,
} from "@pagayo/design/storefront-colors";

/** Legacy V1 background-only contract. Read-only until all tenants are V2. */
export const STOREFRONT_BACKGROUND_PALETTE_IDS = [
  "default", "primaryBlue", "successGreen", "informationYellow", "dangerRed",
  "infoBlue", "memberIndigo", "revolutionaryBlack",
] as const;
export type StorefrontBackgroundPaletteId = (typeof STOREFRONT_BACKGROUND_PALETTE_IDS)[number];
export const STOREFRONT_BACKGROUND_TONES = ["light", "dark"] as const;
export type StorefrontBackgroundTone = (typeof STOREFRONT_BACKGROUND_TONES)[number];
export interface LegacyStorefrontAppearanceSettings {
  backgroundPaletteId: StorefrontBackgroundPaletteId;
  backgroundTone: StorefrontBackgroundTone;
}
export type StorefrontAppearanceSettings = StorefrontColorChoice;
export type StorefrontAppearanceStoredValue = StorefrontColorChoice | LegacyStorefrontAppearanceSettings | null;

export const DEFAULT_STOREFRONT_APPEARANCE_SETTINGS: StorefrontColorChoice = {
  ...DEFAULT_STOREFRONT_COLOR_CHOICE,
};

const LEGACY_COLORS: Readonly<Record<StorefrontBackgroundPaletteId, Readonly<{ light: string | null; dark: string | null }>>> = {
  default: { light: null, dark: null },
  primaryBlue: { light: "#2563eb", dark: "#4f79ff" },
  successGreen: { light: "#22c55e", dark: "#3bd46c" },
  informationYellow: { light: "#f59e0b", dark: "#ffac24" },
  dangerRed: { light: "#ef4444", dark: "#ff5451" },
  infoBlue: { light: "#3b82f6", dark: "#5190ff" },
  memberIndigo: { light: "#6366f1", dark: "#7473ff" },
  revolutionaryBlack: { light: "#0a0a0f", dark: "#12121a" },
};

export function isLegacyStorefrontAppearanceSettings(value: unknown): value is LegacyStorefrontAppearanceSettings {
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  return typeof record.backgroundPaletteId === "string"
    && STOREFRONT_BACKGROUND_PALETTE_IDS.includes(record.backgroundPaletteId as StorefrontBackgroundPaletteId)
    && typeof record.backgroundTone === "string"
    && STOREFRONT_BACKGROUND_TONES.includes(record.backgroundTone as StorefrontBackgroundTone);
}

/** Missing or invalid input deliberately remains null: null means legacy/unmigrated. */
export function normalizeStorefrontAppearanceSettings(input: unknown): StorefrontAppearanceStoredValue {
  if (isStorefrontColorChoice(input)) return normalizeStorefrontColorChoice(input);
  if (isLegacyStorefrontAppearanceSettings(input)) {
    return {
      backgroundPaletteId: input.backgroundPaletteId,
      backgroundTone: input.backgroundTone,
    };
  }
  return null;
}

export function resolveStorefrontAppearanceBackgroundColor(input: unknown): string | null {
  const settings = normalizeStorefrontAppearanceSettings(input);
  if (!settings || !isLegacyStorefrontAppearanceSettings(settings)) return null;
  return LEGACY_COLORS[settings.backgroundPaletteId][settings.backgroundTone];
}

export function buildLegacyStorefrontAppearanceCss(input: unknown): string {
  const backgroundColor = resolveStorefrontAppearanceBackgroundColor(input);
  if (!backgroundColor) return "";
  return [
    `body[data-page-type] { --storefront-page-background: ${backgroundColor}; }`,
    `body[data-page-type]:not([data-page-type="admin"]):not([data-page-type^="admin-"]) { background-color: var(--storefront-page-background); }`,
    `body[data-page-type]:not([data-page-type="admin"]):not([data-page-type^="admin-"]) main { background-color: var(--storefront-page-background); }`,
    `.webshop-layout { background: var(--storefront-page-background, var(--bg-deep)); }`,
  ].join("\n");
}

export function buildStorefrontColorSystemCss(input: unknown): string {
  const settings = normalizeStorefrontAppearanceSettings(input);
  if (!settings || !isStorefrontColorChoice(settings)) return "";
  const tokens = resolveStorefrontColorSystem(settings);
  return `${serializeStorefrontColorSystem(tokens)}\n:root { color-scheme: ${settings.mode}; }`;
}

/** Compatibility alias for existing V1 callers; V2 returns its complete token CSS. */
export function buildStorefrontAppearanceCss(input: unknown): string {
  return buildStorefrontColorSystemCss(input) || buildLegacyStorefrontAppearanceCss(input);
}

export function resolveStorefrontColorTokens(input: unknown): StorefrontColorTokens | null {
  const settings = normalizeStorefrontAppearanceSettings(input);
  return settings && isStorefrontColorChoice(settings)
    ? resolveStorefrontColorSystem(settings)
    : null;
}

export { isStorefrontColorChoice, normalizeStorefrontColorChoice };
export type { StorefrontColorChoice, StorefrontColorTokens };
